import { ZoneImportErrors } from "~/api/Api";
import { duplicateKey } from "~/components/siteSpeed/dns/dnsValidators";
import { type IntegrationType } from "~/components/siteSpeed/dns/dnsValidators";

import { apiDataToChangesMap } from "../helpers";
import type { DnsRecordModel } from "../types";
import { validateImportedRecord } from "./validators";

type GroupedZoneErrors = Record<string, ZoneImportErrors>;

export type ImportResult = {
  records: Map<number, DnsRecordModel>;
  skipped: Array<{
    id: number;
    reason: "validation_failed" | "duplicate";
    errors?: Record<string, string>;
  }>;
};

/**
 * Converts the backend "zone" payload into a Map of DNS record changes for the UI.
 * Each record is normalized, validated, and deduplicated based on (type, nameFQDN, content).
 */
export function buildImportedChanges(
  zonePayload: (string | number)[][],
  domainName: string,
  integrationType: IntegrationType,
): ImportResult {
  const base = apiDataToChangesMap(zonePayload, domainName);

  // collect for tracking duplicates inside import
  const seen = new Set<string>();
  const records = new Map<number, DnsRecordModel>();
  const skipped: ImportResult["skipped"] = [];

  for (const [id, row] of base.entries()) {
    const key = duplicateKey(row.type, row.nameFQDN, row.content);

    // validator context
    const ctx = {
      getType: () => row.type,
      integrationType,
      isDuplicate: () => seen.has(key),
    };
    const toValidate = { type: row.type, ttl: row.ttl, content: row.content };

    const errors = validateImportedRecord(toValidate, ctx);

    if (Object.keys(errors).length > 0) {
      skipped.push({ id, reason: "validation_failed", errors });
      continue;
    }

    if (seen.has(key)) {
      skipped.push({ id, reason: "duplicate" });
      continue;
    }

    seen.add(key);
    records.set(id, row);
  }

  return { records, skipped };
}

export function groupZoneImportErrors(
  errors: ZoneImportErrors,
): GroupedZoneErrors {
  return errors.reduce<GroupedZoneErrors>((acc, err) => {
    const key = err.message || "Unknown error";

    if (!acc[key]) acc[key] = [];
    acc[key].push(err);

    return acc;
  }, {});
}
